import { FC, useState } from "react";
import {
    Badge,
    Box,
    Button,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { IconBuilding, IconChevronDown, IconChevronRight, IconFolder, IconSearch } from "@tabler/icons-react";
import { sortBy } from "lodash";
import { useClientEntities } from "../../api";
import type { EntityResponse } from "../../api/models";

interface BulkMoveModalProps {
    isOpen: boolean;
    onClose: () => void;
    clientId: string;
    selectedCount: number;
    onConfirm: (targetEntityId: string) => void;
}

export function BulkMoveModal({ isOpen, onClose, clientId, selectedCount, onConfirm }: BulkMoveModalProps) {
    const entitiesQuery = useClientEntities(clientId);
    const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>(undefined);
    const [filterTerm, setFilterTerm] = useState("");

    const selectedEntity = selectedEntityId ? findEntityById(entitiesQuery.data ?? [], selectedEntityId) : undefined;

    const handleConfirm = () => {
        if (!selectedEntityId) return;
        onConfirm(selectedEntityId);
        handleClose();
    };

    const handleClose = () => {
        setSelectedEntityId(undefined);
        setFilterTerm("");
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Move {selectedCount} Candidate{selectedCount !== 1 ? "s" : ""} to Another Entity
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={3}>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={IconSearch} color="gray.400" />
                            </InputLeftElement>
                            <Input placeholder="Filter entities…" value={filterTerm} onChange={e => setFilterTerm(e.target.value)} />
                        </InputGroup>

                        <Box borderWidth={1} borderRadius="md" p={2} maxH="320px" overflowY="auto" bg="gray.50">
                            {entitiesQuery.isLoading ? (
                                <Spinner size="sm" m={4} />
                            ) : (
                                sortBy(entitiesQuery.data ?? [], i => (i.entityType === "Directory" ? `A${i.name}` : i.name)).map(entity => (
                                    <EntityTreeItem
                                        key={entity.id}
                                        entity={entity}
                                        selectedEntityId={selectedEntityId}
                                        filterTerm={filterTerm}
                                        onSelect={setSelectedEntityId}
                                    />
                                ))
                            )}
                        </Box>

                        {selectedEntity && (
                            <Box bg="blue.50" borderRadius="md" px={3} py={2}>
                                <Text fontSize="sm" color="gray.600">
                                    Move to:
                                </Text>
                                <HStack>
                                    <Icon as={IconBuilding} color="blue.500" />
                                    <Text fontWeight="semibold" color="blue.700">
                                        {selectedEntity.name}
                                    </Text>
                                </HStack>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={handleConfirm} isDisabled={!selectedEntityId}>
                        Move {selectedCount} Candidate{selectedCount !== 1 ? "s" : ""}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

// ─── Entity Tree ──────────────────────────────────────────────────────────────

interface EntityTreeItemProps {
    entity: EntityResponse;
    selectedEntityId: string | undefined;
    filterTerm: string;
    onSelect: (id: string) => void;
    depth?: number;
}

function matchesFilter(entity: EntityResponse, term: string): boolean {
    if (!term) return true;
    if (entity.name.toLowerCase().includes(term.toLowerCase())) return true;
    return (entity.children ?? []).some(c => matchesFilter(c, term));
}

function findEntityById(entities: EntityResponse[], id: string): EntityResponse | undefined {
    for (const e of entities) {
        if (e.id === id) return e;
        const found = findEntityById(e.children ?? [], id);
        if (found) return found;
    }
    return undefined;
}

const EntityTreeItem: FC<EntityTreeItemProps> = ({ entity, selectedEntityId, filterTerm, onSelect, depth = 0 }) => {
    const isDirectory = entity.entityType === "Directory";
    const hasChildren = isDirectory && (entity.children?.length ?? 0) > 0;
    const isFilterActive = filterTerm.length > 0;
    const isVisible = !isFilterActive || matchesFilter(entity, filterTerm);
    const forceExpand = isFilterActive && isDirectory && (entity.children ?? []).some(c => matchesFilter(c, filterTerm));

    const [isOpen, setIsOpen] = useState(!isDirectory || forceExpand);

    if (!isVisible) return null;

    const isSelected = entity.id === selectedEntityId;
    const sortedChildren = sortBy(entity.children ?? [], c => (c.entityType === "Directory" ? `A${c.name}` : c.name));

    return (
        <Box>
            <HStack
                px={2}
                py={1}
                pl={`${0.5 + depth * 1.25}rem`}
                borderRadius="sm"
                cursor={isDirectory ? "default" : "pointer"}
                bg={isSelected ? "blue.100" : "transparent"}
                _hover={!isDirectory ? { bg: isSelected ? "blue.100" : "gray.100" } : {}}
                onClick={() => {
                    if (isDirectory) {
                        setIsOpen(v => !v);
                    } else {
                        onSelect(entity.id);
                    }
                }}
            >
                <Box minW="1rem">{isDirectory && <Icon as={isOpen ? IconChevronDown : IconChevronRight} boxSize="0.85rem" color="gray.500" />}</Box>
                <Icon as={isDirectory ? IconFolder : IconBuilding} boxSize="1rem" color={isDirectory ? "yellow.500" : isSelected ? "blue.500" : "gray.500"} />
                <Text fontSize="sm" fontWeight={isSelected ? "semibold" : "normal"} color={isSelected ? "blue.700" : "inherit"}>
                    {entity.name}
                </Text>
                {!isDirectory && entity.candidateCount > 0 && (
                    <Badge colorScheme="blue" fontSize="xs">
                        {entity.candidateCount}
                    </Badge>
                )}
            </HStack>

            {hasChildren && isOpen && (
                <Box>
                    {sortedChildren.map(child => (
                        <EntityTreeItem
                            key={child.id}
                            entity={child}
                            selectedEntityId={selectedEntityId}
                            filterTerm={filterTerm}
                            onSelect={onSelect}
                            depth={depth + 1}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
